import React, { useEffect, useState } from "react";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import "./CheckInPage.css";
import "./CheckInHistoryPage.css";

const CheckInHistoryDetailPage = () => {
  const { resultId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!resultId) {
      setLoading(false);
      setError("Result ID is missing.");
      return;
    }

    const loadDetail = async () => {
      try {
        const res = await fetch(`http://localhost:4000/api/checkin/history/${resultId}`, {
        credentials: "include",
        });

        const result = await res.json();

        if (!res.ok) {
          throw new Error(result.message || "Failed to load check-in detail.");
        }

        setData(result);
      } catch (err) {
        console.error("Failed to load check-in detail:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [resultId]);

  if (loading) {
    return (
      <main className="checkin-page">
        <div className="checkin-page__container">
          <p>Loading...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="checkin-page">
        <div className="checkin-page__container">
          <p>{error}</p>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="checkin-page">
        <div className="checkin-page__container">
          <p>No data found.</p>
        </div>
      </main>
    );
  }

  const resultJson = data.result_json || {};
  const triggers = resultJson.triggers || [];
  const actions = resultJson.actions || [];
  const domainBreakdown = resultJson.domain_breakdown || [];

  return (
    <main className="checkin-page">
      <div className="checkin-page__container">
        <Link to="/check-in-history" className="checkin-history-page__back">
          <ArrowLeft aria-hidden="true" />
          Back to Check-in History
        </Link>

        <header className="checkin-page__header">
          <div className="checkin-page__icon"><BarChart3 /></div>
          <h1 className="checkin-page__heading">Check-In Detail</h1>
        </header>

        <section className="checkin-page__section-card">
          <h2 className="checkin-page__section-title">Risk Summary</h2>

          <div
            className={`checkin-page__badge checkin-page__badge--${data.risk_level?.toLowerCase()}`}
          >
            {data.risk_level} Risk
          </div>

          <p className="checkin-page__score">
            Total score: <strong>{data.total_score}</strong>
          </p>

          <p className="history-date">
            {data.created_at ? new Date(data.created_at).toLocaleString() : ""}
          </p>
        </section>

        <section className="checkin-page__section-card">
          <h2 className="checkin-page__section-title">Domain Scores</h2>

          {domainBreakdown.length > 0 ? (
            <ul className="checkin-page__list">
              {domainBreakdown.map((d) => (
                <li key={d.domain_code} className="checkin-page__list-item">
                  <strong>{d.domain_label}</strong>
                  <p>
                    Score: {d.raw_score} / {d.max_score}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p>No domain scores recorded.</p>
          )}
        </section>

        <section className="checkin-page__section-card">
          <h2 className="checkin-page__section-title">Triggers</h2>

          {triggers.length > 0 ? (
            <ul className="checkin-page__list">
              {triggers.map((t, i) => (
                <li key={t.trigger_id || i} className="checkin-page__list-item">
                  <strong>{t.trigger_label || t.trigger_code || "Trigger"}</strong>
                  {t.description && <p>{t.description}</p>}
                </li>
              ))}
            </ul>
          ) : (
            <p>No triggers recorded.</p>
          )}
        </section>

        <section className="checkin-page__section-card">
          <h2 className="checkin-page__section-title">Actions</h2>

          {actions.length > 0 ? (
            <ul className="checkin-page__list">
              {actions.map((a, i) => (
                <li key={a.code || i} className="checkin-page__list-item">
                  <strong>{a.title}</strong>
                  <p>{a.body}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p>No actions recorded.</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default CheckInHistoryDetailPage;
